import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import { FaArrowLeft, FaCalendarAlt, FaExternalLinkAlt } from 'react-icons/fa';

const DEFAULT_IMAGE = 'https://hub.mileageriot.com/assets/images/logo_sm.png';
const DEFAULT_BANNER = 'https://hub.mileageriot.com/assets/images/banner/banner.jpg';

const getImg = (img) => {
  if (!img) return DEFAULT_BANNER;
  if (img.includes('logo_sm.png') || img.includes('logo.svg')) return DEFAULT_BANNER;
  if (img.startsWith('http')) return img;
  return DEFAULT_BANNER;
};

const formatDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('ru', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return ''; }
};

const NewsItem = () => {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const ref = useRef();

  useEffect(() => {
    window.scrollTo(0, 0);
    const cacheKey = `newsV3-${slug}`;
    const cached = localStorage.getItem(cacheKey);
    const cachedTs = localStorage.getItem(`${cacheKey}_ts`);
    const fresh = cachedTs && Date.now() - parseInt(cachedTs) < 6 * 3600 * 1000;

    const applyItem = async (data) => {
      let newsData = data;

      // If TruckersMP source — fetch full content from TMP API
      if (newsData?.source === 'truckersmp' && newsData?.external_id) {
        try {
          const tmpRes = await fetch(`https://api.truckersmp.com/v2/vtc/48336/news/${newsData.external_id}`);
          const tmpJson = await tmpRes.json();
          if (!tmpJson.error && tmpJson.response) {
            newsData = { ...newsData, content: tmpJson.response.content || newsData.content };
          }
        } catch (e) { /* use Hub content as fallback */ }
      }

      setItem(newsData);
      localStorage.setItem(cacheKey, JSON.stringify(newsData));
      localStorage.setItem(`${cacheKey}_ts`, Date.now().toString());
    };

    (async () => {
      try {
        if (cached && fresh) {
          const parsed = JSON.parse(cached);
          // Still re-fetch TMP content if needed (cache may have summary)
          if (parsed?.source === 'truckersmp' && parsed?.external_id &&
              (!parsed.content || parsed.content.length < 100)) {
            await applyItem(parsed);
          } else {
            setItem(parsed);
          }
        } else {
          const res = await fetch(`https://hub.mileageriot.com/api/news/${slug}`);
          const json = await res.json();
          await applyItem(json.data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <section className="news-item-modern page-news-item" ref={ref}>
        <div className="container text-center py-5">
          <div className="loading-spinner"><div className="spinner-modern"></div></div>
          <p className="mt-3 text-white-50">Загружаем новость...</p>
        </div>
      </section>
    );
  }

  if (!item || item.title === 'unknown') {
    return (
      <section className="news-item-modern page-news-item">
        <div className="container text-center py-5">
          <h3 className="text-white">Новость не найдена</h3>
          <Link to="/news" className="btn btn-hero-primary mt-4">← Все новости</Link>
        </div>
      </section>
    );
  }

  const img = getImg(item.image);
  const isTMP = item.source === 'truckersmp';
  const tmpLink = isTMP && item.external_id
    ? `https://truckersmp.com/vtc/48336/news/${item.external_id}`
    : null;
  const metaDesc = item.description || (item.content || '').replace(/<[^>]+>/g, '').substring(0, 155);
  // The same article is reachable as /news/{id} and /news/{slug}. Always point the
  // canonical at the slug form so the two URLs collapse into one indexed page.
  const canonicalUrl = `https://mileageriot.com/news/${item.slug || slug}`;

  return (
    <>
      <Helmet>
        <title>{item.title} — Mileage Riot</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${item.title} — Mileage Riot`} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={img !== DEFAULT_BANNER ? img : DEFAULT_IMAGE} />
        <meta property="og:locale" content="ru_RU" />
      </Helmet>

      <section className="news-item-modern page-news-item">
        <div className="container" style={{ maxWidth: 860 }}>
          {/* Back */}
          <div style={{ marginBottom: 28 }}>
            <Link to="/news" style={{
              color: 'rgba(255,255,255,0.55)', textDecoration: 'none',
              fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 6
            }}>
              <FaArrowLeft style={{ fontSize: '0.8rem' }} /> Все новости
            </Link>
          </div>

          {/* Article */}
          <article>
            {/* Hero image */}
            <div style={{ borderRadius: 18, overflow: 'hidden', marginBottom: 32, position: 'relative' }}>
              <img loading="lazy" decoding="async"
                src={img}
                alt={item.title}
                style={{ width: '100%', maxHeight: 420, objectFit: 'cover', display: 'block' }}
                onError={e => { e.target.src = DEFAULT_BANNER; }}
              />
              {/* MR watermark */}
              <div style={{
                position: 'absolute', bottom: 14, right: 14,
                background: 'rgba(0,0,0,0.65)', borderRadius: 20,
                padding: '5px 12px', display: 'flex', alignItems: 'center', gap: 7,
                backdropFilter: 'blur(6px)', border: '1px solid rgba(0,203,205,0.3)'
              }}>
                <img loading="lazy" decoding="async" src={DEFAULT_IMAGE} alt="MR" style={{ width: 22, height: 22, borderRadius: '50%' }} />
                <span style={{ color: '#fff', fontSize: '0.78rem', fontWeight: 600 }}>Mileage Riot</span>
                {isTMP && <span style={{ color: '#00cbcd', fontSize: '0.72rem', borderLeft: '1px solid rgba(0,203,205,0.4)', paddingLeft: 7 }}>TruckersMP</span>}
              </div>
            </div>

            {/* Meta */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 5 }}>
                <FaCalendarAlt style={{ fontSize: '0.75rem' }} />
                {formatDate(item.created_at || item.published_at)}
              </span>
              {tmpLink && (
                <a href={tmpLink} target="_blank" rel="noopener noreferrer"
                  style={{ color: '#00cbcd', fontSize: '0.82rem', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FaExternalLinkAlt style={{ fontSize: '0.7rem' }} /> TruckersMP
                </a>
              )}
            </div>

            {/* Title */}
            <h1 style={{ color: '#fff', fontSize: 'clamp(1.4rem, 4vw, 2rem)', fontWeight: 700, lineHeight: 1.35, marginBottom: 20 }}>
              {item.title}
            </h1>

            {/* Divider */}
            <div style={{ height: 2, background: 'linear-gradient(90deg, #00cbcd, transparent)', marginBottom: 28, borderRadius: 2 }} />

            {/* Content */}
            {item.source === 'truckersmp' ? (
              <div
                className="text-white news-markdown"
                style={{ fontSize: '1rem', lineHeight: 1.85, wordBreak: 'break-word' }}
              >
                <ReactMarkdown
                  components={{
                    img: ({ src, alt }) => (
                      <img loading="lazy" decoding="async" src={src} alt={alt || ''} style={{ maxWidth: '100%', borderRadius: 8, margin: '12px 0' }} />
                    ),
                    a: ({ href, children }) => (
                      <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#00cbcd' }}>{children}</a>
                    ),
                    p: ({ children }) => <p style={{ marginBottom: 12 }}>{children}</p>,
                    strong: ({ children }) => <strong style={{ color: '#fff', fontWeight: 700 }}>{children}</strong>,
                    em: ({ children }) => <em style={{ color: 'rgba(255,255,255,0.85)' }}>{children}</em>,
                  }}
                >
                  {item.content || item.description || ''}
                </ReactMarkdown>
              </div>
            ) : (
              <div
                className="text-white"
                style={{
                  fontSize: '1rem', lineHeight: 1.85,
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-word'
                }}
                dangerouslySetInnerHTML={{ __html: item.content || `<p>${item.description || ''}</p>` }}
              />
            )}

            {/* Footer */}
            <div style={{
              marginTop: 40, paddingTop: 20,
              borderTop: '1px solid rgba(255,255,255,0.08)',
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', flexWrap: 'wrap', gap: 12
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img loading="lazy" decoding="async" src={DEFAULT_IMAGE} alt="MR" style={{ width: 34, height: 34, borderRadius: '50%' }} />
                <div>
                  <div style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 600 }}>Mileage Riot</div>
                  {isTMP && <div style={{ color: '#00cbcd', fontSize: '0.73rem' }}>via TruckersMP</div>}
                </div>
              </div>
              <Link to="/news" style={{
                background: 'rgba(0,203,205,0.12)', border: '1px solid rgba(0,203,205,0.3)',
                color: '#00cbcd', padding: '8px 18px', borderRadius: 8,
                textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 6
              }}>
                <FaArrowLeft style={{ fontSize: '0.75rem' }} /> Все новости
              </Link>
            </div>
          </article>
        </div>
        <div className="news-item-bg-decoration"></div>
      </section>
    </>
  );
};

export default NewsItem;
